import { useCallback, useState } from 'react';
import { MAX_ITEM_UPGRADE_LEVEL } from '@sorrem/shared';
import { useItemCrafting, type ItemCraftingState } from '@sorrem-client/ui/hooks/use-item-crafting';

export interface CraftingWorkbenchState {
    /** The affix of this item whose reroll the vessel is showing (its link hovered or its confirmation open), or null. */
    resolvePreviewedReroll: (itemId: string, affixCount: number) => number | null;
    /** Shows an affix's reroll in the vessel; null goes back to the upgrade. */
    previewReroll: (itemId: string, affixIndex: number | null) => void;
    /** The level chosen on the upgrade path: one above the item's to upgrade to, or its own (the Current card, the default). */
    resolveTargetLevel: (itemId: string, currentLevel: number) => number;
    chooseTargetLevel: (itemId: string, level: number) => void;
    /** The tab's one crafting state, so an upgrade under way also holds off rerolls, and a failure shows on its own item (#330). */
    crafting: ItemCraftingState;
}

/** A choice made on one item; another item starts from its own default. */
interface ItemChoice {
    readonly itemId: string;
    readonly value: number;
}

/**
 * The Crafting tab's workbench: the level chosen on the upgrade path and the
 * reroll being looked at. Held above the tab's panels, since the workbench
 * chooses and the vessel panel shows what it costs (#287); the level
 * survives switching tabs. Each choice is kept with its item, so picking
 * another item starts on its Current card. It owns the tab's crafting, for
 * every panel that upgrades or rerolls.
 */
export function useCraftingWorkbench(characterId: string | null): CraftingWorkbenchState {
    const crafting = useItemCrafting(characterId);
    const [previewedReroll, setPreviewedReroll] = useState<ItemChoice | null>(null);
    const [targetLevel, setTargetLevel] = useState<ItemChoice | null>(null);

    const resolvePreviewedReroll = useCallback((itemId: string, affixCount: number): number | null => (
        previewedReroll !== null && previewedReroll.itemId === itemId && previewedReroll.value < affixCount ? previewedReroll.value : null
    ), [previewedReroll]);

    const previewReroll = useCallback((itemId: string, value: number | null): void => (
        setPreviewedReroll(value === null ? null : { itemId, value })
    ), []);

    // A chosen level the item has since reached (an upgrade landed) falls back to the item's own.
    const resolveTargetLevel = useCallback((itemId: string, currentLevel: number): number => (
        targetLevel !== null && targetLevel.itemId === itemId && targetLevel.value > currentLevel && targetLevel.value <= MAX_ITEM_UPGRADE_LEVEL
            ? targetLevel.value
            : currentLevel
    ), [targetLevel]);

    const chooseTargetLevel = useCallback((itemId: string, value: number): void => setTargetLevel({ itemId, value }), []);

    return { resolvePreviewedReroll, previewReroll, resolveTargetLevel, chooseTargetLevel, crafting };
}
